import { gql, useMutation } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { FETCH_QUERY_BUILDER_GROUPS_GQL } from '@modules/common/hooks/useFetchQueryBuilderGroupOptions/constants';
import { CreateUnifiedFieldInput, Mutation } from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';
import { LIST_UNIFIED_FIELDS_GQL } from './useFetchUnifiedFields';

const CREATE_UNIFIED_FIELD_GQL = gql`
  mutation createUnifiedField(
    $tenantID: ID!
    $userID: ID!
    $name: String!
    $description: String
    $referencedKeys: [String!]!
    $shouldHideReferencedKeys: Boolean!
    $type: MetadataDataType
  ) {
    createUnifiedField(
      request: {
        tenantID: $tenantID
        userID: $userID
        name: $name
        description: $description
        referencedKeys: $referencedKeys
        shouldHideReferencedKeys: $shouldHideReferencedKeys
        type: $type
      }
    ) {
      success
    }
  }
`;

export const useCreateUnifiedField = () => {
  const {
    tenant: { id: tenantID },
    id: userID,
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const [createUnifiedFieldMutation, { loading }] = useMutation<
    Mutation,
    CreateUnifiedFieldInput
  >(CREATE_UNIFIED_FIELD_GQL, {
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to create Unified Field.',
        props: {},
      });
    },
    refetchQueries: [FETCH_QUERY_BUILDER_GROUPS_GQL, LIST_UNIFIED_FIELDS_GQL],
  });

  const createUnifiedField = async (
    input: Omit<CreateUnifiedFieldInput, 'tenantID' | 'userID'>,
  ) => {
    try {
      return await createUnifiedFieldMutation({
        variables: {
          tenantID,
          userID,
          ...input,
        },
      });
    } catch {
      throw new Error('Failed to create Unified Field.');
    }
  };

  return [
    createUnifiedField,
    {
      loading,
    },
  ] as const;
};
